/**
 * Trainer Participants panel — admit / mute / unmute / allow screen share /
 * remove, plus class-wide mic & video permissions.
 */

import React, { useMemo, useState } from "react";
import {
  useLocalParticipant,
  useParticipants,
} from "@livekit/components-react";
import { Track, type Participant } from "livekit-client";
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  UserCheck,
  UserMinus,
  UserPlus,
  MonitorUp,
  Loader2,
  Settings2,
  X,
  Hand,
  Search,
} from "lucide-react";
import { livekitAuthService as authService } from "../utils/authService";
import {
  isParticipantCamLocked,
  isParticipantMicLocked,
  isParticipantPresenter,
  isParticipantWaiting,
  isRecorderParticipant,
  parseParticipantRole,
} from "./config";
import {
  moderateLiveParticipant,
  moderateLiveParticipantsBulk,
  type ModerationAction,
} from "./participantModerationApi";
import {
  normalizeSessionSettings,
  type LiveSessionSettings,
} from "./sessionSettings";
import type { LiveHandRaisesApi } from "./useLiveHandRaises";

type TrackSource = "microphone" | "camera" | "all";

type Props = {
  sessionId: string | number;
  settings: LiveSessionSettings;
  onSettingsChange: (settings: LiveSessionSettings) => void;
  /** Raised-hand queue owned by room shell so toasts work when panel is closed. */
  handRaises: LiveHandRaisesApi;
  onAfterAction?: (params: {
    action: ModerationAction;
    identity: string;
    trackSource?: TrackSource;
  }) => void;
};

const btnBase: React.CSSProperties = {
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.04)",
  color: "#e2e8f0",
  borderRadius: 8,
  padding: "4px 8px",
  fontSize: 11,
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
};

function classToggleStyle(active: boolean, kind: "locked" | "allowed"): React.CSSProperties {
  const lockedActive = active && kind === "locked";
  const allowedActive = active && kind === "allowed";
  return {
    ...btnBase,
    flex: 1,
    justifyContent: "center",
    minHeight: 34,
    borderRadius: 8,
    border: lockedActive
      ? "1px solid rgba(248,113,113,0.55)"
      : allowedActive
        ? "1px solid rgba(52,211,153,0.5)"
        : "1px solid rgba(255,255,255,0.1)",
    background: lockedActive
      ? "rgba(248,113,113,0.18)"
      : allowedActive
        ? "rgba(16,185,129,0.16)"
        : "rgba(255,255,255,0.03)",
    color: lockedActive ? "#fecaca" : allowedActive ? "#a7f3d0" : "#94a3b8",
    fontWeight: active ? 700 : 600,
    boxShadow: active ? "inset 0 0 0 1px rgba(255,255,255,0.06)" : undefined,
  };
}

function accessSummary(s: LiveSessionSettings): string {
  if (s.students_can_unmute && s.students_can_enable_camera) {
    return "Students: mic + camera";
  }
  if (s.students_can_unmute) return "Students: mic only";
  if (s.students_can_enable_camera) return "Students: camera only";
  return "Students: locked";
}

/** True when LiveKit still allows publishing this source (empty list = unrestricted). */
function canPublishSource(p: Participant, source: Track.Source): boolean {
  const perm = p.permissions;
  if (!perm) return true;
  if (!perm.canPublish) return false;
  const sources = perm.canPublishSources ?? [];
  if (sources.length === 0) return true;
  const proto =
    source === Track.Source.Microphone
      ? 2
      : source === Track.Source.Camera
        ? 1
        : -1;
  return sources.some(
    (s) => (s as unknown) === source || (s as unknown) === proto,
  );
}

export function ParticipantModerationPanel({
  sessionId,
  settings,
  onSettingsChange,
  handRaises,
  onAfterAction,
}: Props) {
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();
  const raisedById = useMemo(() => {
    const map = new Map<string, { name: string; raised_at: number }>();
    for (const h of handRaises.raisedHands) {
      map.set(h.student_id, {
        name: h.student_name,
        raised_at: h.raised_at,
      });
    }
    return map;
  }, [handRaises.raisedHands]);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [removeTarget, setRemoveTarget] = useState<{
    identity: string;
    name: string;
  } | null>(null);

  const approveHand = async (studentId: string, studentName: string) => {
    const key = `${studentId}:approve-hand:microphone`;
    setBusyKey(key);
    setError(null);
    onAfterAction?.({
      action: "unmute",
      identity: studentId,
      trackSource: "microphone",
    });
    try {
      const result = await moderateLiveParticipant({
        sessionId,
        identity: studentId,
        action: "unmute",
        trackSource: "microphone",
      });
      if (!result.ok) {
        setError(`Could not approve mic for ${studentName}`);
        return;
      }
      await handRaises.lowerHand(studentId, studentName);
    } catch (err) {
      console.error("approve hand failed", err);
      setError(`Failed to approve ${studentName}`);
    } finally {
      setBusyKey(null);
    }
  };

  const rows = useMemo(() => {
    return [...participants]
      .filter((p) => !isRecorderParticipant(p.identity, p.metadata))
      .map((p) => {
        const role = parseParticipantRole(p.metadata, p.identity);
        const waiting = isParticipantWaiting(p.metadata);
        const presenter = isParticipantPresenter(p.metadata);
        // Use LiveKit enabled flags — do NOT use publication.track (undefined when
        // selective subscribe skips audio → falsely looked "muted" / only "Allow mic").
        const micOn = Boolean(p.isMicrophoneEnabled);
        const camOn = Boolean(p.isCameraEnabled);
        const micLocked =
          isParticipantMicLocked(p.metadata) ||
          !canPublishSource(p, Track.Source.Microphone);
        const camLocked =
          isParticipantCamLocked(p.metadata) ||
          !canPublishSource(p, Track.Source.Camera);
        const handRaise = raisedById.get(p.identity);
        return {
          identity: p.identity,
          name: p.name || p.identity,
          role,
          waiting,
          presenter,
          micOn,
          camOn,
          micLocked,
          camLocked,
          isLocal: p.identity === localParticipant?.identity,
          isTrainer: role === "trainer",
          handRaised: Boolean(handRaise),
          raisedAt: handRaise?.raised_at ?? Number.POSITIVE_INFINITY,
        };
      })
      .sort((a, b) => {
        // Trainers first, then raised hands (FIFO), then waiting, then name.
        if (a.isTrainer !== b.isTrainer) return a.isTrainer ? -1 : 1;
        if (a.handRaised !== b.handRaised) return a.handRaised ? -1 : 1;
        if (a.handRaised && b.handRaised) return a.raisedAt - b.raisedAt;
        if (a.waiting !== b.waiting) return a.waiting ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
  }, [participants, localParticipant?.identity, raisedById]);

  const studentRows = useMemo(
    () => rows.filter((r) => !r.isTrainer && !r.isLocal),
    [rows],
  );

  const visibleRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = String(r.name || "").toLowerCase();
      const studentId = String(r.identity || "").toLowerCase();
      return name.includes(q) || studentId.includes(q);
    });
  }, [rows, searchQuery]);

  const activePresenterIdentities = useMemo(
    () => rows.filter((r) => r.presenter && !r.isTrainer).map((r) => r.identity),
    [rows],
  );

  /** Class media state follows session policy (trainer-controlled). */
  const classMicsLocked = !settings.students_can_unmute;
  const classCamsLocked = !settings.students_can_enable_camera;

  const run = async (
    identity: string,
    action: ModerationAction,
    trackSource: TrackSource = "microphone",
  ) => {
    const key = `${identity}:${action}:${trackSource}`;
    setBusyKey(key);
    setError(null);
    // Soft notice fans out immediately; LiveKit ACL still follows REST.
    if (action === "promote") {
      for (const prev of activePresenterIdentities) {
        if (prev === identity) continue;
        onAfterAction?.({ action: "demote", identity: prev });
      }
    }
    onAfterAction?.({ action, identity, trackSource });
    try {
      const result = await moderateLiveParticipant({
        sessionId,
        identity,
        action,
        trackSource,
      });
      if (!result.ok) {
        setError(`Could not ${action} ${identity}`);
        return;
      }
    } catch (err) {
      console.error("moderation failed", err);
      setError(`Failed to ${action}`);
    } finally {
      setBusyKey(null);
    }
  };

  const moderateAll = async (
    action: ModerationAction,
    trackSource: TrackSource,
  ) => {
    if (action !== "mute" && action !== "unmute") return;
    const identities = studentRows
      .filter((r) => !r.waiting)
      .map((r) => r.identity);
    if (identities.length === 0) return;
    const key = `all:${action}:${trackSource}`;
    setBusyKey(key);
    setError(null);
    onAfterAction?.({
      action,
      identity: "*",
      trackSource,
    });
    try {
      const result = await moderateLiveParticipantsBulk({
        sessionId,
        action,
        trackSource,
        identities,
      });
      if (!result.ok && (result.failed || 0) > 0) {
        setError(
          `Updated ${result.succeeded}/${result.targeted} students; ${result.failed} failed`,
        );
      }
    } catch (err) {
      console.error("bulk media moderation failed", err);
      setError("Failed to update all students");
    } finally {
      setBusyKey(null);
    }
  };

  const persistSettings = async (next: LiveSessionSettings) => {
    setSaving(true);
    setError(null);
    try {
      const payload = normalizeSessionSettings(next);
      // Optimistic UI — keep trainer choice until server confirms.
      onSettingsChange(payload);
      const res = await authService.put("/session/update/", {
        session_id: String(sessionId),
        session_settings: payload,
      });
      if (res?.data?.success === false) {
        setError(res?.data?.message || "Failed to save media permissions");
        onSettingsChange(settings);
        return null;
      }
      const saved = normalizeSessionSettings(
        res?.data?.session?.session_settings ?? payload,
      );
      onSettingsChange(saved);
      return saved;
    } catch (err) {
      console.error("save media permissions failed", err);
      setError("Could not save media permissions");
      onSettingsChange(settings);
      return null;
    } finally {
      setSaving(false);
    }
  };

  /** Class-wide mute/allow — single control; persists until trainer changes it. */
  const setClassTrackAllowed = async (
    trackSource: TrackSource,
    allow: boolean,
  ) => {
    const field =
      trackSource === "microphone"
        ? "students_can_unmute"
        : "students_can_enable_camera";
    const next = { ...settings, [field]: allow };
    const saved = await persistSettings(next);
    if (!saved) return;
    await moderateAll(allow ? "unmute" : "mute", trackSource);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minHeight: 0,
        height: "100%",
      }}
    >
      <div
        style={{
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.1)",
          background: "#1a1d26",
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={() => setSettingsOpen((v) => !v)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            border: "none",
            background: "transparent",
            padding: "10px 12px",
            cursor: "pointer",
            color: "inherit",
            font: "inherit",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12,
              fontWeight: 700,
              color: "#e2e8f0",
            }}
          >
            <Settings2 size={14} color="#60a5fa" />
            Mic &amp; video permissions
          </span>
          <span style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>
            {saving ? "Saving…" : accessSummary(settings)}
          </span>
        </button>
        {settingsOpen ? (
          <div
            style={{
              borderTop: "1px solid rgba(255,255,255,0.1)",
              padding: 12,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#94a3b8",
                    marginBottom: 6,
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                  }}
                >
                  Class microphones —{" "}
                  <span
                    style={{
                      color: classMicsLocked ? "#fca5a5" : "#6ee7b7",
                    }}
                  >
                    {classMicsLocked ? "Muted / locked" : "Allowed"}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 0,
                    borderRadius: 10,
                    overflow: "hidden",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                  role="group"
                  aria-label="Class microphone state"
                >
                  <button
                    type="button"
                    style={{
                      ...classToggleStyle(classMicsLocked, "locked"),
                      borderRadius: 0,
                      border: "none",
                      borderRight: "1px solid rgba(255,255,255,0.08)",
                    }}
                    disabled={!!busyKey || saving}
                    aria-pressed={classMicsLocked}
                    title="Mute and lock all student microphones"
                    onClick={() => {
                      if (classMicsLocked) return;
                      void setClassTrackAllowed("microphone", false);
                    }}
                  >
                    {busyKey === "all:mute:microphone" ||
                    (saving && !classMicsLocked) ? (
                      <Loader2 size={12} />
                    ) : (
                      <MicOff size={12} />
                    )}
                    Mute all
                  </button>
                  <button
                    type="button"
                    style={{
                      ...classToggleStyle(!classMicsLocked, "allowed"),
                      borderRadius: 0,
                      border: "none",
                    }}
                    disabled={!!busyKey || saving}
                    aria-pressed={!classMicsLocked}
                    title="Allow all students to use their microphone"
                    onClick={() => {
                      if (!classMicsLocked) return;
                      void setClassTrackAllowed("microphone", true);
                    }}
                  >
                    {busyKey === "all:unmute:microphone" ||
                    (saving && classMicsLocked) ? (
                      <Loader2 size={12} />
                    ) : (
                      <Mic size={12} />
                    )}
                    Allow all
                  </button>
                </div>
              </div>

              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#94a3b8",
                    marginBottom: 6,
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                  }}
                >
                  Class cameras —{" "}
                  <span
                    style={{
                      color: classCamsLocked ? "#fca5a5" : "#6ee7b7",
                    }}
                  >
                    {classCamsLocked ? "Stopped / locked" : "Allowed"}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 0,
                    borderRadius: 10,
                    overflow: "hidden",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                  role="group"
                  aria-label="Class camera state"
                >
                  <button
                    type="button"
                    style={{
                      ...classToggleStyle(classCamsLocked, "locked"),
                      borderRadius: 0,
                      border: "none",
                      borderRight: "1px solid rgba(255,255,255,0.08)",
                    }}
                    disabled={!!busyKey || saving}
                    aria-pressed={classCamsLocked}
                    title="Turn off and lock all student cameras"
                    onClick={() => {
                      if (classCamsLocked) return;
                      void setClassTrackAllowed("camera", false);
                    }}
                  >
                    {busyKey === "all:mute:camera" ||
                    (saving && !classCamsLocked) ? (
                      <Loader2 size={12} />
                    ) : (
                      <VideoOff size={12} />
                    )}
                    Stop all
                  </button>
                  <button
                    type="button"
                    style={{
                      ...classToggleStyle(!classCamsLocked, "allowed"),
                      borderRadius: 0,
                      border: "none",
                    }}
                    disabled={!!busyKey || saving}
                    aria-pressed={!classCamsLocked}
                    title="Allow all students to use their camera"
                    onClick={() => {
                      if (!classCamsLocked) return;
                      void setClassTrackAllowed("camera", true);
                    }}
                  >
                    {busyKey === "all:unmute:camera" ||
                    (saving && classCamsLocked) ? (
                      <Loader2 size={12} />
                    ) : (
                      <Video size={12} />
                    )}
                    Allow all
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {error ? (
        <p style={{ margin: 0, fontSize: 12, color: "#fca5a5", flexShrink: 0 }}>
          {error}
        </p>
      ) : null}

      {handRaises.raisedHands.length > 0 ? (
        <div
          style={{
            borderRadius: 12,
            border: "1px solid rgba(251,191,36,0.35)",
            background: "rgba(251,191,36,0.08)",
            padding: 10,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              fontWeight: 700,
              color: "#fde68a",
            }}
          >
            <Hand size={14} />
            Raised hands ({handRaises.raisedHands.length})
          </div>
          {handRaises.raisedHands.map((h) => (
            <div
              key={h.student_id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  color: "#e2e8f0",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {h.student_name}
              </span>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button
                  type="button"
                  style={{
                    ...btnBase,
                    borderColor: "rgba(52,211,153,0.45)",
                    color: "#6ee7b7",
                  }}
                  disabled={!!busyKey}
                  onClick={() =>
                    void approveHand(h.student_id, h.student_name)
                  }
                >
                  {busyKey === `${h.student_id}:approve-hand:microphone` ? (
                    <Loader2 size={12} />
                  ) : (
                    <Mic size={12} />
                  )}
                  Approve mic
                </button>
                <button
                  type="button"
                  style={btnBase}
                  disabled={!!busyKey}
                  onClick={() =>
                    void handRaises.lowerHand(h.student_id, h.student_name)
                  }
                >
                  Lower
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexShrink: 0,
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.12)",
          background: "rgba(15,20,28,0.65)",
          padding: "8px 10px",
        }}
      >
        <Search size={14} color="#64748b" />
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name or student ID"
          aria-label="Search participants by name or student ID"
          style={{
            flex: 1,
            minWidth: 0,
            border: "none",
            outline: "none",
            background: "transparent",
            color: "#e2e8f0",
            fontSize: 13,
            fontWeight: 600,
          }}
        />
        {searchQuery.trim() ? (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            aria-label="Clear search"
            style={{
              border: "none",
              background: "transparent",
              color: "#94a3b8",
              cursor: "pointer",
              padding: 0,
              lineHeight: 0,
            }}
          >
            <X size={14} />
          </button>
        ) : null}
      </div>

      <div
        className="meeting-tool-hide-scrollbar"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {rows.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13 }}>No participants connected yet.</p>
        ) : visibleRows.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>
            No participants match “{searchQuery.trim()}”.
          </p>
        ) : (
          visibleRows.map((row) => {
            const busy = busyKey?.startsWith(`${row.identity}:`);
            const handUp = raisedById.has(row.identity);
            return (
              <div
                key={row.identity}
                style={{
                  padding: 10,
                  borderRadius: 10,
                  border: handUp
                    ? "1px solid rgba(251,191,36,0.4)"
                    : "1px solid rgba(255,255,255,0.08)",
                  background: row.waiting
                    ? "rgba(245,158,11,0.08)"
                    : handUp
                      ? "rgba(251,191,36,0.06)"
                      : "rgba(255,255,255,0.03)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    marginBottom: row.isTrainer || row.isLocal ? 0 : 8,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#fff",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {handUp ? (
                        <Hand
                          size={12}
                          style={{
                            display: "inline",
                            marginRight: 6,
                            color: "#fbbf24",
                            verticalAlign: "middle",
                          }}
                        />
                      ) : null}
                      {row.name}
                      {row.isLocal ? " (you)" : ""}
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                      {row.isTrainer
                        ? "Trainer"
                        : row.waiting
                          ? "Waiting"
                          : row.presenter
                            ? "Presenter"
                            : "Student"}
                      {!row.isTrainer ? ` · ${row.identity}` : ""}
                      {handUp ? " · Hand raised" : ""}
                      {" · "}
                      {row.micOn ? "Mic on" : "Mic off"}
                      {row.micLocked ? " (locked)" : ""}
                      {" · "}
                      {row.camOn ? "Cam on" : "Cam off"}
                      {row.camLocked ? " (locked)" : ""}
                    </div>
                  </div>
                  {busy ? (
                    <Loader2 size={14} style={{ color: "#94a3b8" }} />
                  ) : null}
                </div>

                {!row.isTrainer && !row.isLocal ? (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                    }}
                  >
                    {row.waiting ? (
                      <button
                        type="button"
                        style={btnBase}
                        disabled={!!busyKey}
                        onClick={() => void run(row.identity, "admit")}
                      >
                        <UserCheck size={12} /> Admit
                      </button>
                    ) : (
                      <button
                        type="button"
                        style={btnBase}
                        disabled={!!busyKey}
                        onClick={() => void run(row.identity, "hold")}
                      >
                        <UserMinus size={12} /> Hold
                      </button>
                    )}
                    <button
                      type="button"
                      style={btnBase}
                      disabled={!!busyKey}
                      title={
                        row.micLocked
                          ? "Allow this student to use their microphone"
                          : "Mute and lock this student's microphone"
                      }
                      onClick={() =>
                        void run(
                          row.identity,
                          row.micLocked ? "unmute" : "mute",
                          "microphone",
                        )
                      }
                    >
                      {row.micLocked ? <Mic size={12} /> : <MicOff size={12} />}
                      {row.micLocked ? "Allow mic" : "Mute mic"}
                    </button>
                    <button
                      type="button"
                      style={btnBase}
                      disabled={!!busyKey}
                      title={
                        row.camLocked
                          ? "Allow this student to use their camera"
                          : "Turn off and lock this student's camera"
                      }
                      onClick={() =>
                        void run(
                          row.identity,
                          row.camLocked ? "unmute" : "mute",
                          "camera",
                        )
                      }
                    >
                      {row.camLocked ? (
                        <Video size={12} />
                      ) : (
                        <VideoOff size={12} />
                      )}
                      {row.camLocked ? "Allow cam" : "Stop cam"}
                    </button>
                    {row.presenter ? (
                      <button
                        type="button"
                        style={btnBase}
                        disabled={!!busyKey}
                        onClick={() => void run(row.identity, "demote")}
                      >
                        <MonitorUp size={12} /> Revoke screen share
                      </button>
                    ) : (
                      <button
                        type="button"
                        style={btnBase}
                        disabled={!!busyKey || row.waiting}
                        title={
                          activePresenterIdentities.length
                            ? "Only one student can share at a time — this replaces the current presenter"
                            : undefined
                        }
                        onClick={() => void run(row.identity, "promote")}
                      >
                        <UserPlus size={12} /> Allow screen share
                      </button>
                    )}
                    <button
                      type="button"
                      style={{
                        ...btnBase,
                        borderColor: "rgba(248,113,113,0.4)",
                        color: "#fca5a5",
                      }}
                      disabled={!!busyKey}
                      onClick={() =>
                        setRemoveTarget({
                          identity: row.identity,
                          name: row.name,
                        })
                      }
                    >
                      Remove
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>

      {removeTarget ? (
        <div
          role="presentation"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(15, 23, 42, 0.65)",
            backdropFilter: "blur(4px)",
            padding: 16,
          }}
          onClick={() => setRemoveTarget(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-participant-title"
            style={{
              width: "100%",
              maxWidth: 400,
              borderRadius: 14,
              background: "#1a1d26",
              border: "1px solid rgba(255,255,255,0.12)",
              boxShadow: "0 25px 50px -12px rgba(0,0,0,0.55)",
              padding: 20,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 8,
                marginBottom: 10,
              }}
            >
              <h2
                id="remove-participant-title"
                style={{
                  margin: 0,
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#fff",
                }}
              >
                Remove participant?
              </h2>
              <button
                type="button"
                onClick={() => setRemoveTarget(null)}
                aria-label="Close"
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#94a3b8",
                  cursor: "pointer",
                  padding: 4,
                  lineHeight: 0,
                }}
              >
                <X size={18} />
              </button>
            </div>
            <p
              style={{
                margin: "0 0 18px",
                fontSize: 13,
                color: "#94a3b8",
                lineHeight: 1.45,
              }}
            >
              Remove{" "}
              <strong style={{ color: "#e2e8f0" }}>{removeTarget.name}</strong>{" "}
              from the live room? They will need to rejoin and be admitted again.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={() => setRemoveTarget(null)}
                style={{
                  ...btnBase,
                  padding: "8px 14px",
                  fontSize: 12,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!!busyKey}
                onClick={() => {
                  const identity = removeTarget.identity;
                  setRemoveTarget(null);
                  void run(identity, "remove");
                }}
                style={{
                  ...btnBase,
                  padding: "8px 14px",
                  fontSize: 12,
                  borderColor: "rgba(248,113,113,0.5)",
                  background: "rgba(220,38,38,0.25)",
                  color: "#fecaca",
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
